import React from 'react';
import { Service, QuestionnaireTemplate } from '@/types';
import { Edit2, FileText, Trash2 } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';
import { useServicesPage, useServiceMutations, type ServiceListParams } from '@features/admin/data';

interface ServicesListProps {
  page: number;
  pageSize: number;
  search: string;
  canEdit: boolean;
  questionnaireOptions: QuestionnaireTemplate[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (service: Service) => void;
}

export const ServicesList: React.FC<ServicesListProps> = ({
  page,
  pageSize,
  search,
  canEdit,
  questionnaireOptions,
  onPageChange,
  onPageSizeChange,
  onEdit
}) => {
  const serviceMutations = useServiceMutations();
  const params: ServiceListParams = { page, pageSize, q: search || undefined };
  const result = useServicesPage(params);
  const services = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.length === 0 ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No services found.
          </div>
        ) : (
          services.map((service) => {
            const linkedQ = questionnaireOptions.find(
              (q) => q.id === service.questionnaireConfig?.templateId
            );
            return (
              <div
                key={service.id}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative group flex flex-col justify-between hover:shadow-xl transition-all"
              >
                <div>
                  {canEdit && (
                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onEdit(service)}
                        className="p-2 bg-white/90 rounded-full border border-slate-100 shadow-sm text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => serviceMutations.remove(service.id)}
                        className="p-2 bg-white/90 rounded-full border border-slate-100 shadow-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <h4 className="font-bold text-lg text-slate-900 mb-1">{service.name}</h4>
                  <p className="text-slate-500 text-sm mb-6 h-10 line-clamp-2 leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div>
                  {linkedQ ? (
                    <div className="mb-4 bg-teal-50 border border-teal-100 p-2.5 rounded-xl flex items-start gap-3">
                      <div className="bg-teal-600 p-1.5 rounded-lg text-white">
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase font-black text-teal-700 tracking-widest mb-0.5">
                          Form Assigned
                        </div>
                        <div className="text-[11px] font-bold text-teal-900 leading-tight">
                          {linkedQ.name}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 bg-slate-50 border border-slate-100 p-2.5 rounded-xl flex items-start gap-3 opacity-60">
                      <div className="bg-slate-300 p-1.5 rounded-lg text-white">
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-0.5">
                          No Form Required
                        </div>
                        <div className="text-[11px] font-bold text-slate-500 leading-tight italic">
                          Direct assessment only
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-4 border-t border-slate-50">
                    <span className="font-black text-slate-900 text-lg">£{service.price}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-2.5 py-1 rounded-full">
                      {service.durationMinutes} MINS
                    </span>
                  </div>
                  {service.tierPrices &&
                    Object.values(service.tierPrices).some((v) => v != null) && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {service.tierPrices.bronze != null && (
                          <span className="bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded text-[10px] font-bold">
                            B: £{service.tierPrices.bronze}
                          </span>
                        )}
                        {service.tierPrices.silver != null && (
                          <span className="bg-slate-50 text-slate-600 border border-slate-100 px-2 py-0.5 rounded text-[10px] font-bold">
                            S: £{service.tierPrices.silver}
                          </span>
                        )}
                        {service.tierPrices.gold != null && (
                          <span className="bg-yellow-50 text-yellow-700 border border-yellow-100 px-2 py-0.5 rounded text-[10px] font-bold">
                            G: £{service.tierPrices.gold}
                          </span>
                        )}
                        {service.tierPrices.platinum != null && (
                          <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded text-[10px] font-bold">
                            P: £{service.tierPrices.platinum}
                          </span>
                        )}
                        {service.tierPrices.diamond != null && (
                          <span className="bg-cyan-50 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded text-[10px] font-bold">
                            D: £{service.tierPrices.diamond}
                          </span>
                        )}
                      </div>
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[25, 50, 100]}
      />
    </>
  );
};
